import { EffectsPanel } from "../effects/EffectsPanel";
import { HalftoneControls } from "../effects/HalftoneControls";
import { NoiseControls } from "../effects/NoiseControls";
import { useEffectStack } from "../effects/useEffectStack";
import { ExportPanel } from "../export/ExportPanel";
import { MediaDropzone } from "../media/MediaDropzone";
import { PreviewStage } from "../media/PreviewStage";
import { useMediaProject } from "../media/useMediaProject";

export function WorkspaceShell() {
  const { state, importFile, clear } = useMediaProject();
  const effects = useEffectStack();
  const hasVideo = state.status === "ready" && state.asset.kind === "video";
  const asset = state.status === "ready" ? state.asset : null;
  const selectedEffect = effects.selected;

  return (
    <main className="workspace-page" id="workspace">
      <section className="workspace-intro" aria-labelledby="alterly-title">
        <p className="eyebrow">SHAKL ONLINE TOOLS / ALTERLY</p>
        <h1 id="alterly-title">Visual effects and motion, built to stay in flow.</h1>
        <p>
          A local-first workspace for image, video, vector effects, motion, and production export.
        </p>
      </section>

      <section className="workspace-frame" aria-label="Alterly workspace">
        <aside className="workspace-panel workspace-panel--left">
          <EffectsPanel
            effects={effects.effects}
            selectedId={selectedEffect?.instanceId ?? null}
            canUndo={effects.canUndo}
            canRedo={effects.canRedo}
            onAdd={effects.add}
            onSelect={effects.select}
            onToggle={effects.toggle}
            onDuplicate={effects.duplicate}
            onRemove={effects.remove}
            onMove={effects.move}
            onUndo={effects.undo}
            onRedo={effects.redo}
          />
        </aside>

        <div className="workspace-canvas">
          {asset ? (
            <PreviewStage asset={asset} onClear={clear} effects={effects.effects} />
          ) : (
            <div className="canvas-stage">
              <MediaDropzone disabled={state.status === "loading"} onFile={importFile} />
              {state.status === "error" && <p className="media-error" role="alert">{state.error}</p>}
            </div>
          )}
        </div>

        <aside className="workspace-panel workspace-panel--right">
          {selectedEffect ? (
            <>
              <div className="effect-opacity">
                <span>Opacity <b>{Math.round(selectedEffect.opacity * 100)}%</b></span>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={selectedEffect.opacity}
                  onChange={(event) => effects.updateOpacity(selectedEffect.instanceId, Number(event.target.value))}
                />
              </div>

              {selectedEffect.definitionId === "halftone" ? (
                <HalftoneControls
                  parameters={selectedEffect.parameters}
                  onChange={(patch) => effects.updateHalftone(selectedEffect.instanceId, patch)}
                  onReset={effects.resetSelected}
                />
              ) : (
                <NoiseControls
                  parameters={selectedEffect.parameters}
                  onChange={(patch) => effects.updateNoise(selectedEffect.instanceId, patch)}
                  onReset={effects.resetSelected}
                />
              )}
            </>
          ) : (
            <div className="panel-placeholder">Select or add an effect to edit parameters.</div>
          )}

          {asset && (
            <dl className="media-inspector media-inspector--compact">
              <div><dt>Type</dt><dd>{asset.kind}</dd></div>
              <div><dt>Format</dt><dd>{asset.mimeType || "Unknown"}</dd></div>
              <div><dt>Size</dt><dd>{asset.dimensions.width} × {asset.dimensions.height}</dd></div>
            </dl>
          )}

          <ExportPanel asset={asset} effects={effects.effects} />
        </aside>

        <div className={`workspace-timeline${hasVideo ? " is-active" : ""}`}>
          <div className="panel-label">Timeline</div>
          {hasVideo ? (
            <div className="timeline-track timeline-track--active" aria-label="Video timeline placeholder">
              <span>Video loaded · deterministic timeline engine next</span>
            </div>
          ) : (
            <div className="timeline-track" />
          )}
        </div>
      </section>
    </main>
  );
}
